import {
  AnalyticsClient,
  ConsoleAnalyticsSink,
  Ga4AnalyticsSink,
  getStoredConsent,
  hasAnalyticsConsent,
  loadGa4Script,
  setStoredConsent,
  clearStoredConsent,
  type ConsentChoice,
  type ConsentState
} from "@reno-estimator/analytics";

/**
 * The one place this app wires up analytics, mirroring estimatorClient.ts
 * and emailEstimateClient.ts's pattern. GA4 is only ever integrated when a
 * measurement id is actually configured (via `window.RenoEstimatorConfig`,
 * the same global the WordPress embed already uses for the pricing REST
 * URL - see estimatorClient.ts) - nothing here silently reaches out to
 * Google's servers by default. No property sent to any sink can carry
 * personal information: see `@reno-estimator/analytics`'s
 * `scrubEventProperties`, applied unconditionally to every event by
 * `AnalyticsClient` before it reaches a sink.
 */
// `window.RenoEstimatorConfig`'s type is declared once, in
// estimatorClient.ts (RenoEstimatorConfig), and reused here.

let cachedClient: AnalyticsClient | null = null;
let consentState: ConsentState = getStoredConsent();
let ga4Loaded = false;

function ga4MeasurementId(): string | undefined {
  return typeof window !== "undefined" ? window.RenoEstimatorConfig?.ga4MeasurementId : undefined;
}

function ensureGa4LoadedIfConsented(): void {
  const measurementId = ga4MeasurementId();
  if (!measurementId || !hasAnalyticsConsent(consentState) || ga4Loaded) return;
  loadGa4Script(measurementId);
  ga4Loaded = true;
}

/**
 * The shared AnalyticsClient instance. Always logs to the console sink
 * in dev-style builds (never a third-party destination, so it needs no
 * consent) and forwards to GA4 only when both a measurement id is
 * configured AND the visitor has granted analytics consent.
 */
export function getAnalyticsClient(): AnalyticsClient {
  if (cachedClient) return cachedClient;
  const measurementId = ga4MeasurementId();
  cachedClient = new AnalyticsClient({
    // Console logging never leaves the browser and carries the same
    // PII-scrubbed payload as any other sink, so it needs no consent
    // gate - useful during local dev/QA to see events firing without
    // needing a real GA4 property configured.
    alwaysOnSinks: [new ConsoleAnalyticsSink()],
    consentGatedSinks: measurementId ? [new Ga4AnalyticsSink({ measurementId })] : [],
    hasConsent: () => hasAnalyticsConsent(consentState)
  });
  ensureGa4LoadedIfConsented();
  return cachedClient;
}

export function getAnalyticsConsentState(): ConsentState {
  return consentState;
}

export function setAnalyticsConsent(choice: Exclude<ConsentChoice, "undecided">): ConsentState {
  consentState = setStoredConsent(choice);
  if (choice === "granted") ensureGa4LoadedIfConsented();
  return consentState;
}

export function resetAnalyticsConsent(): void {
  clearStoredConsent();
  consentState = getStoredConsent();
}
